import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { api, API_ORIGEM } from '../lib/api';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

// Tela "Perfis XMLs" do legado (`admin/xml/index.blade.php`) — só superadmin.
// Legado: uma tabela com os 10 perfis (nome, descrição, inclusão, última
// sincronização, prefixo) e 3 ações por linha: Exportar (gerar), Atualizar
// prefixo (só o OLX) e Visualizar o XML.
//
// Diferença de arquitetura (decisão de Jason 2026-09-18, painel-node 1.0.0):
// o "Geral" continua combinado (todas as empresas); os outros 9 são gerados
// POR EMPRESA (cada imobiliária tem sua conta em cada portal). Por isso, além
// da tabela, há o bloco "Feeds de uma empresa".
const ARQUIVOS = {
  1: 'olx.xml', 2: 'geral.xml', 3: 'campainha.xml', 4: 'vivareal.xml', 5: 'antigo_vivareal.xml',
  6: 'imovomap.xml', 7: 'zipanuncios.xml', 8: 'facebook.xml', 9: 'trovit.xml', 10: 'homer.xml',
};

function formatarData(iso, comHora) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return '—';
  return comHora ? `${m[3]}/${m[2]}/${m[1]} às ${m[4]}:${m[5]}:${m[6]}` : `${m[3]}/${m[2]}/${m[1]}`;
}

export default function FeedsXml() {
  const toast = useToast();
  const confirmar = useConfirm();
  const [perfis, setPerfis] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [ocupado, setOcupado] = useState('');
  const [resultado, setResultado] = useState(null);

  function carregar() {
    api
      .get('/xml')
      .then((r) => setPerfis(r.data))
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar os perfis XML'));
  }

  useEffect(() => {
    carregar();
    api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function gerarGeral() {
    setOcupado('geral');
    try {
      const r = await api.post('/xml/gerar-geral');
      if (r.ok) toast.sucesso(`Feed Geral gerado (${r.total_imoveis} imóveis).`);
      else toast.erro(r.erro || 'Não foi possível gerar o feed Geral');
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível gerar o feed Geral');
    } finally {
      setOcupado('');
    }
  }

  async function gerarEmpresa() {
    if (!empresaId) return;
    setOcupado('empresa');
    setResultado(null);
    try {
      const r = await api.post(`/xml/empresas/${empresaId}/gerar`);
      setResultado(r.resultados);
      const falhas = r.resultados.filter((x) => !x.ok).length;
      if (falhas === 0) toast.sucesso('Feeds da empresa gerados.');
      else toast.erro(`${falhas} feed(s) com problema — veja a lista abaixo.`);
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível gerar os feeds');
    } finally {
      setOcupado('');
    }
  }

  async function atualizarPrefixo(perfil) {
    if (!(await confirmar('Atualizar o prefixo do OLX? Os anúncios passam a ser tratados como novos pelo portal.'))) return;
    setOcupado(`prefixo-${perfil.id}`);
    try {
      await api.post(`/xml/${perfil.id}/prefixo`);
      toast.sucesso('Prefixo atualizado com sucesso!');
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível atualizar o prefixo');
    } finally {
      setOcupado('');
    }
  }

  const empresaSelecionada = empresas.find((e) => e.public_id === empresaId);

  return (
    <>
      <h1 className="h4 mb-4">Feeds XML</h1>

      <Alert variant="light" className="border small">
        O feed <strong>Geral</strong> reúne todas as empresas. Os demais formatos (OLX, VivaReal, etc.) são gerados{' '}
        <strong>por empresa</strong> — escolha a empresa mais abaixo. Todo dia às 22h os feeds das empresas que tiveram alteração são
        regenerados automaticamente.
      </Alert>

      <Card className="mb-4">
        <Card.Header>Perfis XML</Card.Header>
        <Card.Body className="p-0">
          {!perfis && <Spinner animation="border" className="m-3" />}
          {perfis && (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>XML</th>
                  <th>Descrição</th>
                  <th>Inclusão</th>
                  <th>Última sinc.</th>
                  <th>Prefixo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {perfis.map((p) => (
                  <tr key={p.id}>
                    <td>{p.xml_name}</td>
                    <td>{p.xml_descricao}</td>
                    <td>{formatarData(p.created_at, false)}</td>
                    <td>{formatarData(p.xml_sincro, true)}</td>
                    <td>{p.xml_codigo || <Badge bg="warning" text="dark">não configurado</Badge>}</td>
                    <td className="text-end text-nowrap">
                      {p.id === 2 && (
                        <>
                          <Button size="sm" variant="outline-primary" className="me-1" disabled={!!ocupado} onClick={gerarGeral}>
                            {ocupado === 'geral' ? <Spinner animation="border" size="sm" /> : 'Gerar'}
                          </Button>
                          <Button size="sm" variant="outline-secondary" as="a" target="_blank" rel="noreferrer" href={`${API_ORIGEM}/xml/${ARQUIVOS[2]}`}>
                            Visualizar
                          </Button>
                        </>
                      )}
                      {p.id === 1 && (
                        <Button size="sm" variant="outline-warning" disabled={!!ocupado} onClick={() => atualizarPrefixo(p)}>
                          {ocupado === `prefixo-${p.id}` ? <Spinner animation="border" size="sm" /> : 'Atualizar prefixo'}
                        </Button>
                      )}
                      {p.id !== 2 && <span className="text-muted small ms-2">por empresa ↓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Feeds de uma empresa</Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end mb-3">
            <Col md={5}>
              <Form.Group controlId="empresa-feed">
                <Form.Label>Empresa</Form.Label>
                <Form.Select value={empresaId} onChange={(e) => { setEmpresaId(e.target.value); setResultado(null); }}>
                  <option value="">Selecione...</option>
                  {empresas.map((emp) => (
                    <option key={emp.public_id} value={emp.public_id}>
                      {emp.e1_nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button variant="primary" disabled={!empresaId || !!ocupado} onClick={gerarEmpresa}>
                {ocupado === 'empresa' ? <Spinner animation="border" size="sm" /> : 'Gerar todos os feeds desta empresa'}
              </Button>
            </Col>
          </Row>

          {empresaSelecionada && (
            <Table size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Resultado da última geração</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.entries(ARQUIVOS)
                  .filter(([id]) => Number(id) !== 2)
                  .map(([id, arquivo]) => {
                    const r = resultado?.find((x) => x.id === Number(id));
                    return (
                      <tr key={id}>
                        <td>{arquivo}</td>
                        <td>
                          {r ? (r.ok ? <Badge bg="success">{r.total_imoveis} imóveis</Badge> : <Badge bg="danger">{r.erro}</Badge>) : <span className="text-muted small">—</span>}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-secondary" as="a" target="_blank" rel="noreferrer" href={`${API_ORIGEM}/xml/${empresaId}/${arquivo}`}>
                            Visualizar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
